import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save, Loader2, User, Phone, Home, HeartHandshake, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Two modes:
 *  - Self edit: /profile (no :id in URL) — any signed-in user edits their own profile.
 *  - Admin edit: /admin/team/:userId/hr-info — platform admin edits another user's HR info.
 */
const UserProfilePage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { api, user } = useAuth();
  const isAdminEdit = Boolean(userId);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = isAdminEdit ? `/admin/team/${userId}/hr-info` : '/me/profile';
      const res = await api.get(url);
      setProfile(res.data);
    } catch (err) {
      toast.error('Could not load profile');
    } finally {
      setLoading(false);
    }
  }, [api, isAdminEdit, userId]);

  useEffect(() => { load(); }, [load]);

  const setField = (path, value) => {
    setProfile((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        ref[keys[i]] = { ...(ref[keys[i]] || {}) };
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const url = isAdminEdit ? `/admin/team/${userId}/hr-info` : '/me/profile';
      const payload = {
        name: profile.name,
        phone: profile.phone,
        date_of_birth: profile.date_of_birth,
        address: profile.address || {},
        emergency_contact: profile.emergency_contact || {},
        job_title: profile.job_title,
        department: profile.department,
        start_date: profile.start_date,
        manager: profile.manager,
      };
      await api.put(url, payload);
      toast.success('Profile saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (!profile) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="profile-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <Link
              to={isAdminEdit ? '/admin' : '/dashboard'}
              className="inline-block hover:opacity-70 transition-opacity"
              data-testid="profile-title-link"
            >
              <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
                <User className="w-6 h-6 text-indigo-600" />
                {isAdminEdit ? `Edit HR info — ${profile.name || profile.email}` : 'My Profile & HR Info'}
              </h1>
            </Link>
            <p className="text-sm text-slate-500">
              {isAdminEdit ? 'Update this employee\'s HR information.' : 'Keep your contact and emergency info current so HR can reach you.'}
            </p>
          </div>
          <Button onClick={save} disabled={saving} data-testid="profile-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
        {!isAdminEdit && (
          <Card className="border-2 border-indigo-200 bg-indigo-50/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <User className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-slate-800 text-sm">Two-factor authentication</p>
                <p className="text-xs text-slate-500">Add an authenticator app (Google Authenticator, Authy) for extra security.</p>
              </div>
              <Link to="/security/2fa">
                <Button variant="outline" size="sm" data-testid="profile-2fa-link">Set up 2FA</Button>
              </Link>
            </CardContent>
          </Card>
        )}
        <Section title="Personal Information" icon={User}>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Full name" value={profile.name} onChange={(v) => setField('name', v)} testId="profile-name" />
            <Field label="Email (read-only)" value={profile.email} readOnly />
            <Field label="Phone" value={profile.phone} onChange={(v) => setField('phone', v)} icon={Phone} testId="profile-phone" placeholder="+1 555 123 4567" />
            <Field label="Date of birth" value={profile.date_of_birth} onChange={(v) => setField('date_of_birth', v)} type="date" testId="profile-dob" />
          </div>
        </Section>

        <Section title="Address" icon={Home}>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Street" value={profile.address?.line1} onChange={(v) => setField('address.line1', v)} testId="profile-addr-line1" />
            <Field label="Apt / Suite" value={profile.address?.line2} onChange={(v) => setField('address.line2', v)} />
            <Field label="City" value={profile.address?.city} onChange={(v) => setField('address.city', v)} testId="profile-addr-city" />
            <Field label="State / Region" value={profile.address?.state} onChange={(v) => setField('address.state', v)} />
            <Field label="ZIP / Postal code" value={profile.address?.postal_code} onChange={(v) => setField('address.postal_code', v)} />
            <Field label="Country" value={profile.address?.country} onChange={(v) => setField('address.country', v)} />
          </div>
        </Section>

        <Section title="Emergency Contact" icon={HeartHandshake}>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Name" value={profile.emergency_contact?.name} onChange={(v) => setField('emergency_contact.name', v)} testId="profile-ec-name" />
            <Field label="Phone" value={profile.emergency_contact?.phone} onChange={(v) => setField('emergency_contact.phone', v)} testId="profile-ec-phone" />
            <Field label="Relationship" value={profile.emergency_contact?.relationship} onChange={(v) => setField('emergency_contact.relationship', v)} placeholder="Spouse, parent…" />
          </div>
        </Section>

        {(profile.is_staff || profile.intern_role || profile.is_admin || isAdminEdit) && (
          <Section title="Employment (HR only)" icon={Briefcase}>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Job title" value={profile.job_title} onChange={(v) => setField('job_title', v)} testId="profile-jobtitle" />
              <Field label="Department" value={profile.department} onChange={(v) => setField('department', v)} />
              <Field label="Start date" value={profile.start_date} onChange={(v) => setField('start_date', v)} type="date" />
              <Field label="Manager" value={profile.manager} onChange={(v) => setField('manager', v)} placeholder="Manager's email or name" />
            </div>
          </Section>
        )}
      </main>
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <Card>
    <CardContent className="p-5 space-y-3">
      <h2 className="font-bold text-slate-800 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        {title}
      </h2>
      {children}
    </CardContent>
  </Card>
);

const Field = ({ label, value, onChange, readOnly, testId, placeholder, type = 'text' }) => (
  <div>
    <Label className="text-xs uppercase font-semibold text-slate-500">{label}</Label>
    <Input
      className="mt-1"
      value={value || ''}
      onChange={(e) => onChange && onChange(e.target.value)}
      readOnly={readOnly}
      placeholder={placeholder}
      type={type}
      data-testid={testId}
    />
  </div>
);

export default UserProfilePage;
